import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Template } from '@/api/apiClient';
import apiClient from '@/api/apiClient';
import Thumbnail from '@/components/shared/Thumbnail';
import { Sparkles, Search } from 'lucide-react';
import { useTranslation } from '@/i18n/useTranslation';
import Skeleton from '@/components/shared/Skeleton';
import { matchesTemplateType, toCanonicalTemplateType } from '@/utils/templateType';
import { useAppNavigate } from '@/hooks/useAppNavigate';

type TemplateCategory = 'all' | 'packshot' | 'photography' | 'illustration' | 'infographic' | 'icon';

/**
 * Templates Page - displays all available templates in a grid layout
 * Similar to Gallery but for templates
 */
export default function TemplatesPage() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState<TemplateCategory>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useAppNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useTranslation();

  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const templatesData = await apiClient.getTemplates();
        if (templatesData) {
          // Filter out 'auto' template
          const filteredTemplates = templatesData.filter(t => t.id !== 'auto');
          setTemplates(filteredTemplates);
        }
      } catch (error) {
        console.error('[TemplatesPage] Failed to load templates:', error);
      } finally {
        setLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Filter templates by category and search
  const filteredTemplates = useMemo(() => {
    let filtered = templates;

    // Filter by category
    if (activeCategory !== 'all') {
      filtered = filtered.filter(template => {
        const templateType = toCanonicalTemplateType(template.type);
        if (activeCategory === 'packshot') {
          return matchesTemplateType(templateType, ['packshot', 'product']);
        }
        return matchesTemplateType(templateType, [activeCategory]);
      });
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(template =>
        template.name.toLowerCase().includes(query) ||
        template.description?.toLowerCase().includes(query)
      );
    }

    return filtered;
  }, [templates, activeCategory, searchQuery]);

  // Get available categories from templates
  const categories: { id: TemplateCategory; label: string; count: number }[] = useMemo(() => {
    const cats: { id: TemplateCategory; label: string; count: number }[] = [
      { id: 'all', label: 'All', count: templates.length }
    ];

    const packshotCount = templates.filter(t => matchesTemplateType(t.type, ['packshot', 'product'])).length;
    const photoCount = templates.filter(t => matchesTemplateType(t.type, ['photography'])).length;
    const illustrationCount = templates.filter(t => matchesTemplateType(t.type, ['illustration'])).length;
    const infographicCount = templates.filter(t => matchesTemplateType(t.type, ['infographic'])).length;
    const iconCount = templates.filter(t => matchesTemplateType(t.type, ['icon'])).length;

    if (packshotCount > 0) cats.push({ id: 'packshot', label: 'Packshots', count: packshotCount });
    if (photoCount > 0) cats.push({ id: 'photography', label: 'Photos', count: photoCount });
    if (illustrationCount > 0) cats.push({ id: 'illustration', label: 'Illustrations', count: illustrationCount });
    if (infographicCount > 0) cats.push({ id: 'infographic', label: 'Infographics', count: infographicCount });
    if (iconCount > 0) cats.push({ id: 'icon', label: 'Icons', count: iconCount });

    return cats;
  }, [templates]);

  const handleTemplateClick = (template: Template) => {
    // Get current imageStyle from URL or default to 'photo'
    const currentImageStyle = searchParams.get('imageStyle') || 'photo';
    
    // Navigate to create page with template selected
    const params = new URLSearchParams();
    params.set('imageStyle', currentImageStyle);
    params.set('template', template.id);
    
    navigate(`/create?${params.toString()}`);
  };

  return (
    <div className="h-full w-full bg-bg-main overflow-y-auto">
      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-text-primary mb-2">{t('templates.heading')}</h1>
          <p className="text-sm text-text-secondary mb-4">
            Choose a template to start creating your image
          </p>

          {/* Search Filter */}
          <div className="relative mb-4">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-text-secondary" />
            <input
              type="text"
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 bg-bg-control border border-border rounded-lg text-sm text-text-primary placeholder-text-secondary focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
            />
          </div>

          {/* Category Tabs */}
          <div className="flex flex-wrap gap-2 mb-6">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                  activeCategory === category.id
                    ? 'bg-accent text-bg-main'
                    : 'bg-bg-control text-text-secondary hover:bg-bg-control-hover hover:text-text-primary'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="flex flex-col">
                <Skeleton className="w-full aspect-[4/3] rounded-lg mb-2" />
                <Skeleton className="w-full h-4 rounded" />
              </div>
            ))}
          </div>
        )}

        {/* Templates Grid */}
        {!loading && filteredTemplates.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
            {filteredTemplates.map((template) => (
              <button
                key={template.id}
                onClick={() => handleTemplateClick(template)}
                className="group flex flex-col rounded-lg overflow-hidden border border-border hover:border-accent transition-all bg-bg-panel hover:bg-bg-control cursor-pointer"
              >
                {/* Thumbnail - Card Image */}
                <div className="w-full aspect-[4/3] relative overflow-hidden bg-bg-control">
                  <Thumbnail
                    thumbnailId={template.thumbnail || null}
                    fallbackIcon={
                      <div className="w-full h-full flex items-center justify-center">
                        <Sparkles className="w-8 h-8 text-text-secondary" strokeWidth={2} />
                      </div>
                    }
                    className="w-full h-full"
                  />
                </div>

                {/* Template Info - Below Image */}
                <div className="p-3 flex flex-col gap-1">
                  <div className="text-sm font-medium text-text-primary line-clamp-1 text-left">
                    {template.name}
                  </div>
                  {template.description && (
                    <div className="text-xs text-text-secondary line-clamp-2 text-left">
                      {template.description}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        {/* Empty State */}
        {!loading && filteredTemplates.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <Sparkles className="w-12 h-12 text-text-secondary mb-4" strokeWidth={1.5} />
            <p className="text-text-secondary">
              {searchQuery || activeCategory !== 'all' 
                ? 'No templates found matching your filters' 
                : 'No templates available'}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
